/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  RowData,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import {useState, FC, useEffect, useMemo, forwardRef, useImperativeHandle} from 'react'
import {Row, Col, Pagination, Form, Spinner, Dropdown, Button} from 'react-bootstrap'
import {ListAgreementResultRow} from '../core/_models'
import {Link, useNavigate} from 'react-router-dom'
import {getISO2ByISO3} from '../../../../libs/misc'
import {useAbility} from '@casl/react'
import {AbilityContext} from '../../../../modules/auth/core/AbilityContext'
import withReactContent from 'sweetalert2-react-content'
import Swal from 'sweetalert2'
import {deleteAgreement, exportAgreements} from '../core/_requests'
import {axiosPrivate} from '../../../../libs/axios'

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue> {
    headerClass: string
    cellClass: string
  }
}

type ListAgreementTableProps = {
  rows?: ListAgreementResultRow[] | null
  isLoading: boolean
  onAgreementDelete: (deletedId: number | string) => void
}

type StatusType = 'under_negotiation' | 'signed' | 'inactive' | 'inforce' | null

const ReactSwal = withReactContent(Swal)
const confirmDialog = (title: string) => {
  return ReactSwal.fire({
    title,
    icon: 'question',
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    customClass: {
      title: 'fw-bold',
      cancelButton: 'btn btn-danger btn-sm',
      confirmButton: 'btn btn-success btn-sm',
    },
    padding: '1rem',
  })
}

const ListAgreementNewTable: FC<ListAgreementTableProps & {ref?: any}> = forwardRef(
  ({rows, isLoading, onAgreementDelete}, ref) => {
    const ability = useAbility(AbilityContext)
    const [internalLoading, setInternalLoading] = useState(false)
    const [listAgreement, setListAgreement] = useState<ListAgreementResultRow[]>([])
    // const [selectedSort, setSelectedSort] = useState<string | null>(null)
    // const [displaySortText, setDisplaySortText] = useState('Sort by Country')

    const [pagination, setPagination] = useState({pageIndex: 0, pageSize: 10})

    const [sorting, setSorting] = useState<SortingState>([])
    const [globalFilter, setGlobalFilter] = useState('')
    const [selectedStatus, setSelectedStatus] = useState<StatusType>(null)

    // const getSortParam = (sort: string | null) => {
    //   switch (sort) {
    //     case 'country_asc':
    //       return {
    //         displayText: 'Country Ascending',
    //         sorting: [{id: 'country', desc: false}],
    //       }
    //     case 'country_desc':
    //       return {
    //         displayText: 'Country Descending',
    //         sorting: [{id: 'country', desc: true}],
    //       }
    //     default:
    //       return {
    //         displayText: 'Sort by Country',
    //         sorting: [],
    //       }
    //   }
    // }

    const handleSelectFilter = (status) => {
      if (selectedStatus !== status) {
        setPagination({
          pageIndex: 0,
          pageSize: 10,
        })
      }
      setSelectedStatus(status === 'reset' ? null : status)
    }

    const getDisplayFilterText = () => {
      switch (selectedStatus) {
        case 'under_negotiation':
          return 'Under Negotiation'
        case 'signed':
          return 'Signed'
        case 'inactive':
          return 'Inactive'
        case 'inforce':
          return 'In Force'
        default:
          return 'Filter by Status'
      }
    }

    const columnHelper = createColumnHelper<ListAgreementResultRow>()

    const columns = [
      columnHelper.accessor('acronym', {
        header: 'Short Name',
        cell: (info) => {
          return info.getValue()
        },
        meta: {
          headerClass: 'ps-4 text-start w-100px',
          cellClass: 'text-start w-100px',
        },
      }),
      columnHelper.accessor('name', {
        header: 'Name',
        cell: (info) => {
          return info.getValue()
        },
        meta: {
          headerClass: 'text-start w-200px',
          cellClass: 'text-start w-200px',
        },
        sortUndefined: 'last',
      }),
      columnHelper.accessor('iso', {
        header: 'Parties',
        cell: (info) => {
          if (!info.getValue()) return <span>-</span>
          const countries = info.getValue().split(';')
          return (
            <div className='countries'>
              {countries.map((rawCountry: string, idx: number) => {
                const country = rawCountry.trim()
                const iso2 = getISO2ByISO3(country)

                // 🔑 Explicit EUN handling OR fallback
                return country === 'EUN' || !iso2 ? (
                  <span key={idx} className='badge text-white bg-dark me-1' title={country}>
                    {country}
                  </span>
                ) : (
                  <span key={idx} className={`fi fi-${iso2.toLowerCase()} me-2`} title={country} />
                )
              })}
            </div>
          )
        },
        meta: {
          headerClass: 'text-start w-250px',
          cellClass: 'text-start w-250px',
        },
        sortUndefined: 'last',
      }),
      columnHelper.accessor('date_signed', {
        header: 'Date Signed',
        cell: (info) => {
          return info.getValue()
        },
        meta: {
          headerClass: 'text-start w-100px',
          cellClass: 'text-start w-100px',
        },
        sortUndefined: 'last',
      }),
      columnHelper.accessor('date_into_force', {
        header: 'Date Into Force',
        cell: (info) => {
          return info.getValue() ?? '-'
        },
        meta: {
          headerClass: 'text-start w-100px',
          cellClass: 'text-start w-100px',
        },
        sortUndefined: 'last',
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: (info) => {
          return info.getValue()
        },
        meta: {
          headerClass: 'text-center w-75px',
          cellClass: 'text-center w-75px',
        },
        sortUndefined: 'last',
      }),
      columnHelper.accessor('id', {
        header: 'Actions',
        cell: (info) => {
          return (
            <div className='d-flex gap-3 justify-content-center'>
              {ability.can('read', 'MENU.NEW_RTA') ? (
                <Link to={`/new-rta/${info.getValue()}`} className='link-primary'>
                  <span className='fa fa-eye'></span>
                </Link>
              ) : (
                ''
              )}
              {ability.can('delete', 'MENU.NEW_RTA') ? (
                <Link
                  to='#'
                  className='link-danger'
                  onClick={() => handleAgreementDeletion(info.getValue())}
                >
                  <span className='fa fa-trash'></span>
                </Link>
              ) : (
                ''
              )}
            </div>
          )
        },
        meta: {
          headerClass: '',
          cellClass: 'text-center w-50px',
        },
        enableSorting: false,
      }),
    ]

    const handleExport = async (exportType: 'current-page' | 'all') => {
      try {
        const params =
          exportType === 'current-page'
            ? {
                search: globalFilter || undefined,
                status: selectedStatus ? getDisplayFilterText() : undefined,
                page: pagination.pageIndex,
                pageSize: pagination.pageSize,
              }
            : {
                search: globalFilter || undefined,
                status: selectedStatus ? getDisplayFilterText() : undefined,
              }

        const response = await exportAgreements(axiosPrivate, exportType, params)

        // Trigger browser download
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
          'download',
          exportType === 'current-page' ? 'trade-agreements-page.xlsx' : 'trade-agreements-all.xlsx'
        )
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.error('Export failed:', error)
      }
    }

    useImperativeHandle(ref, () => ({
      handleExport,
    }))

    const handleAgreementDeletion = (id: number | string) => {
      confirmDialog('Are you sure you want to delete this agreement?').then((result) => {
        if (!result.isConfirmed) return

        deleteAgreement(axiosPrivate, id).then((res) => {
          if (res.status === 200) {
            ReactSwal.fire({
              title: 'Trade Agreement deleted',
              icon: 'success',
              confirmButtonText: 'OK',
            }).then(() => {
              onAgreementDelete(id)
            })
          }
        })
      })
    }

    // Cleanup function to reset the state when the component is unmounted
    useEffect(() => {
      setListAgreement([])
      setInternalLoading(true)
      return () => {
        setListAgreement([])
        setInternalLoading(false)
      }
    }, [])

    useEffect(() => {
      setInternalLoading(isLoading)
    }, [isLoading])

    useEffect(() => {
      setListAgreement(rows!)
      setInternalLoading(false)
    }, [rows])

    const filteredData = useMemo(() => {
      if (!rows) return []

      return rows.filter((row) => {
        if (selectedStatus === 'under_negotiation') return row.status === 'Under Negotiation'
        if (selectedStatus === 'signed') return row.status === 'Signed'
        if (selectedStatus === 'inactive') return row.status === 'Inactive'
        if (selectedStatus === 'inforce') return row.status === 'In Force'
        return true
      })
    }, [rows, selectedStatus])

    const dataTable = useReactTable({
      data: filteredData || [],
      columns,
      state: {
        pagination,
        sorting,
        globalFilter,
      },
      pageCount: Math.ceil(filteredData.length / pagination.pageSize),
      onSortingChange: setSorting,
      onPaginationChange: setPagination,
      onGlobalFilterChange: setGlobalFilter,
      getCoreRowModel: getCoreRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
    })

    function paginationRange() {
      const currentPage = pagination.pageIndex + 1
      const totalPages = dataTable.getPageCount()
      const maxPagesToShow = 3
      const halfRange = Math.floor(maxPagesToShow / 2)

      // Ensure startPage and endPage are within valid range
      let startPage = Math.max(1, currentPage - halfRange)
      let endPage = Math.min(totalPages, currentPage + halfRange)

      // Adjust range to maintain maxPagesToShow
      if (endPage - startPage + 1 < maxPagesToShow) {
        if (currentPage - startPage >= halfRange) {
          startPage = Math.max(1, endPage - maxPagesToShow + 1)
        } else {
          endPage = Math.min(totalPages, startPage + maxPagesToShow - 1)
        }
      }

      const range: any = []

      // Add first page and ellipsis if necessary
      if (startPage > 1) {
        range.push(1)
        if (startPage > 2) {
          range.push('...')
        }
      }

      // Add pages in the calculated range
      for (let i = startPage; i <= endPage; i++) {
        range.push(i)
      }

      // Add last page and ellipsis if necessary
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          range.push('...')
        }
        range.push(totalPages)
      }

      return range
    }

    const handleNavigationPageChange = (nav) => {
      setPagination({
        ...pagination,
        pageIndex:
          nav === 'prev' ? Math.max(0, pagination.pageIndex - 1) : pagination.pageIndex + 1,
      })
    }

    const handleDisplayPageChange = (e) => {
      setPagination({
        ...pagination,
        pageIndex: 1,
        pageSize: +e.target.value,
      })
    }

    const pages = paginationRange()
    const firstIndexPerPages =
      dataTable.getState().pagination.pageIndex * dataTable.getState().pagination.pageSize + 1

    const lastIndexPerPages =
      dataTable.getState().pagination.pageIndex * dataTable.getState().pagination.pageSize +
      dataTable.getRowModel().rows.length

    const displayHeader = dataTable.getHeaderGroups().map((headerGroup) => (
      <tr className='fw-normal py-4 align-middle' key={headerGroup.id}>
        {headerGroup.headers.map((header) => {
          return (
            <th key={header.id} className={header.column.columnDef.meta?.headerClass}>
              <div
                className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                title={
                  header.column.getCanSort()
                    ? header.column.getNextSortingOrder() === 'asc'
                      ? 'Sort ascending'
                      : header.column.getNextSortingOrder() === 'desc'
                      ? 'Sort descending'
                      : 'Reset'
                    : undefined
                }
                onClick={header.column.getToggleSortingHandler()}
              >
                {flexRender(header.column.columnDef.header, header.getContext())}
                {header.column.getCanSort() && (
                  <span className='ms-2'>
                    {{
                      asc: <i className='fa-solid fa-sort-up'></i>,
                      desc: <i className='fa-solid fa-sort-down'></i>,
                      false: <i className='fa-solid fa-sort'></i>,
                    }[header.column.getIsSorted() as string] ?? (
                      <i className='fa-solid fa-sort'></i>
                    )}
                  </span>
                )}
              </div>
            </th>
          )
        })}
      </tr>
    ))
    const displayBody =
      dataTable.getRowModel().rows.length > 0 ? (
        dataTable.getRowModel().rows.map((row) => (
          <tr key={row.id} className='align-middle'>
            {row.getVisibleCells().map((cell) => {
              return (
                <td key={cell.id} className={cell.column.columnDef.meta?.cellClass}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              )
            })}
          </tr>
        ))
      ) : (
        <tr className='align-middle'>
          <td colSpan={99} className='text-center'>
            No Data
          </td>
        </tr>
      )

    return (
      <div style={{minHeight: '400px', position: 'relative'}}>
        {!internalLoading ? (
          <>
            <Row className='mb-5 justify-content-between'>
              <Col lg={5} md={5} className='d-flex justify-content-start align-items-center'>
                <i className='fa-solid fa-circle text-success fs-9 me-2'></i>Showing{' '}
                {filteredData.length > 0 ? firstIndexPerPages : 0} to{' '}
                {filteredData.length > 0 ? lastIndexPerPages : 0} of {filteredData.length} results
              </Col>
              <Col lg={7} md={7} className='action-table'>
                <Form.Group className='form-group' controlId=''>
                  <div className='input-with-icon'>
                    <Form.Control
                      type='text'
                      className='form-control-md'
                      onChange={(e) => {
                        setPagination({
                          pageIndex: 0,
                          pageSize: 10,
                        })
                        setGlobalFilter(e.target.value)
                      }}
                      placeholder='Search by Name'
                      value={globalFilter}
                    />
                    <div className='input-icon'>
                      <i className='fa-solid fa-magnifying-glass fa-rotate-90 text-primary'></i>
                    </div>
                  </div>
                </Form.Group>
                <Form.Group className='form-group' controlId=''>
                  <div className='input-with-icon'>
                    <Dropdown onSelect={handleSelectFilter}>
                      <Dropdown.Toggle
                        variant='light'
                        className='text-capitalize w-100 pe-10 text-start'
                      >
                        {getDisplayFilterText()}
                      </Dropdown.Toggle>

                      <Dropdown.Menu>
                        <Dropdown.Item eventKey='under_negotiation'>
                          Under Negotiation
                        </Dropdown.Item>
                        <Dropdown.Item eventKey='signed'>Signed</Dropdown.Item>
                        <Dropdown.Item eventKey='inactive'>Inactive</Dropdown.Item>
                        <Dropdown.Item eventKey='inforce'>In Force</Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item eventKey='reset' className='text-danger'>
                          Reset Filter
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>

                    <div className='input-icon'>
                      <i className='fa-solid fa-filter text-primary'></i>
                    </div>
                  </div>
                </Form.Group>
              </Col>
            </Row>
            <table
              className='gy-3 table table-striped table-bordered table-hover table-header-sticky'
              id='list_country_tradeflow'
            >
              <thead className='table-dark'>{displayHeader}</thead>
              <tbody className='gy-1 overflow-auto'>{displayBody}</tbody>
            </table>
            <Row className='my-3'>
              <Col md={6} className='d-flex justify-content-start align-items-center'>
                Display
                <Form.Select
                  size='sm'
                  className='w-80px mx-2'
                  onChange={handleDisplayPageChange}
                  value={pagination.pageSize}
                >
                  <option value='10'>10</option>
                  <option value='20'>20</option>
                  <option value='50'>50</option>
                  <option value='100'>100</option>
                </Form.Select>
                list country
              </Col>
              <Col md={6} className='d-flex justify-content-end'>
                <Pagination>
                  <Pagination.Prev
                    onClick={() => handleNavigationPageChange('prev')}
                    disabled={pagination.pageIndex === 0}
                  />
                  {/* Start Dynamic Page */}
                  {pages.map((page, index) => {
                    if (page === '...') {
                      return <Pagination.Ellipsis key={index} />
                    }
                    return (
                      <Pagination.Item
                        key={index}
                        active={page === pagination.pageIndex + 1}
                        onClick={() => {
                          setPagination({
                            ...pagination,
                            pageIndex: page - 1, // Convert 1-based page to 0-based index
                          })
                        }}
                      >
                        {page}
                      </Pagination.Item>
                    )
                  })}
                  {/* End Dynamic Page */}
                  <Pagination.Next
                    onClick={() => handleNavigationPageChange('next')}
                    disabled={pagination.pageIndex >= dataTable.getPageCount() - 1}
                  />
                </Pagination>
              </Col>
            </Row>
          </>
        ) : (
          <div
            className='d-flex align-items-center justify-content-center'
            style={{height: '50vh'}}
          >
            <Spinner animation='border' role='status' variant='primary' className='me-3' />
            <div className='loading-text'>
              Loading
              <span className='dot'>.</span>
              <span className='dot'>.</span>
              <span className='dot'>.</span>
            </div>
          </div>
        )}
      </div>
    )
  }
)

export {ListAgreementNewTable as ListAgreementTable}
